import { ClassName, Theme } from '@busybusy/webapp-react-ui';
import { GpsIcon } from 'assets/icons';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentRibbon from 'components/foundation/dashboard/DashboardContentRibbon/DashboardContentRibbon';
import DashboardRibbonGroup from 'components/foundation/dashboard/DashboardRibbonGroup/DashboardRibbonGroup';
import useDashboardGraphDates from 'containers/dashboard/hooks/useDashboardGraphDates';
import { DashboardTimeFrame } from 'containers/dashboard/types/types';
import { convertDashboardTimeFrameToTimeRangeType, getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import { StatusOptions } from 'containers/gps-outside-project-report/GpsOutsideProjectReportSidePanel/GpsOutsideProjectReportSidePanel';
import { getDashboardNavigationRange } from 'containers/scheduling/utils/utils';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useGPSOutsideProjectReportNavigation from 'hooks/navigation/useGPSOutsideProjectReportNavigation';
import { t } from 'i18next';
import { last } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import ITimeRange from 'types/TimeRange';
import { useFeatureFlags } from 'utils/features';
import DashboardProUpgradeCard from '../DashboardProUpgradeCard/DashboardProUpgradeCard';

export interface IPendingGPSOutsideProjectDashboardCardProps {
  onHide?: () => void;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  className?: ClassName;
}

const PendingGPSOutsideProjectDashboardCard = (props: IPendingGPSOutsideProjectDashboardCardProps) => {
  const { className, theme, onHide, setting } = props;

  const isPro = useFeatureFlags('PRO');

  const currentTimeFrame: DashboardTimeFrame = setting.options?.time ?? 'payPeriod';
  const { timeRange, timeRanges } = useDashboardGraphDates(currentTimeFrame);

  const getData = usePendingGpsOutsideProjectData(last(timeRanges));
  const [loading, setLoading] = useState(false);
  const [pendingReview, setPendingReview] = useState<string>('0');
  const [authorized, setAuthorized] = useState<string>('0');
  const [unauthorized, setUnauthorized] = useState<string>('0');
  const navigateToReport = useGPSOutsideProjectReportNavigation();

  useEffect(() => {
    if (!isPro) return;
    setLoading(true);
    getData().then((counts) => {
      setPendingReview(counts.pendingReview.toString());
      setAuthorized(counts.authorized.toString());
      setUnauthorized(counts.unauthorized.toString());
      setLoading(false);
    });
  }, [getData, isPro]);

  function handleNavigation(status: StatusOptions) {
    const navRange = getDashboardNavigationRange(timeRange, timeRanges, setting.options?.time ?? 'payPeriod', 'utc');

    navigateToReport({
      start_date: navRange.startTime.toISODate(),
      end_date: navRange.endTime.toISODate(),
      time_range_type: convertDashboardTimeFrameToTimeRangeType(setting.options?.time ?? 'payPeriod'),
      flagged_location_status: status,
    });
  }

  const classes = classNames('pending-gps-outside-project-dashboard-card', className);

  return (
    <>
      {isPro ? (
        <DashboardRibbonGroup className={classes}>
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => handleNavigation(StatusOptions.PENDING_REVIEW)}
            title={t('GPS Pending Review')}
            subtitle={getDashboardTimeFrameTitle('', currentTimeFrame)}
            value={pendingReview.toString()}
            isLoading={loading}
          />
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => handleNavigation(StatusOptions.AUTHORIZED)}
            title={t('GPS Authorized')}
            subtitle={getDashboardTimeFrameTitle('', currentTimeFrame)}
            value={authorized.toString()}
            isLoading={loading}
          />
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => handleNavigation(StatusOptions.UNAUTHORIZED)}
            title={t('GPS Unauthorized')}
            subtitle={getDashboardTimeFrameTitle('', currentTimeFrame)}
            value={unauthorized.toString()}
            isLoading={loading}
          />
        </DashboardRibbonGroup>
      ) : (
        <DashboardProUpgradeCard title={setting.title} svg={GpsIcon} theme={theme} onHide={onHide} />
      )}
    </>
  );
};

const GPS_OUTSIDE_PROJECT_COUNTS_QUERY = gql`
  query GpsOutsideProjectCounts($startTime: Timestamp!, $endTime: Timestamp!) {
    dashboard {
      gpsOutsideProjectCounts(startTime: $startTime, endTime: $endTime) {
        pendingReview
        authorized
        unauthorized
      }
    }
  }
`;

interface IGpsOutsideProjectCounts {
  pendingReview: number;
  authorized: number;
  unauthorized: number;
}

const EMPTY_COUNTS: IGpsOutsideProjectCounts = { pendingReview: 0, authorized: 0, unauthorized: 0 };

// Mirrors the report query's boundary handling: the UTC-zoned range is shifted by the local offset so the
// window matches what the old query sent (and therefore the counts line up).
function toReportTimestamp(seconds: number): string {
  const dateTime = DateTime.fromSeconds(seconds, { zone: 'utc' });
  // `seconds` is always a valid epoch, so toISO never returns null here; assert to match the schema's
  // non-null Timestamp! variables.
  return dateTime.minus(dateTime.toLocal().offset * 60 * 1000).toISO({ suppressSeconds: true })!;
}

export function usePendingGpsOutsideProjectData(range: ITimeRange<number> | undefined) {
  const graphQLClient = useGraphQLClient();

  return useCallback(async (): Promise<IGpsOutsideProjectCounts> => {
    if (!range) return EMPTY_COUNTS;

    const result = await graphQLClient.request<{ dashboard: { gpsOutsideProjectCounts: IGpsOutsideProjectCounts } }>({
      document: GPS_OUTSIDE_PROJECT_COUNTS_QUERY,
      variables: {
        startTime: toReportTimestamp(range.startTime),
        endTime: toReportTimestamp(range.endTime),
      },
    });

    return result.dashboard.gpsOutsideProjectCounts;
    // `range` (last of the value-stable timeRanges) keeps a stable identity, so depending on it
    // directly doesn't refire the query unnecessarily.
  }, [graphQLClient, range]);
}

export default PendingGPSOutsideProjectDashboardCard;
